// CastingCallApplication.js - OPTIMIZED WITH SEARCH & FIXED APPLICATION LOGIC
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Collapse,
  Fade,
  Zoom,
  Paper,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Message as MessageIcon,
  Login as LoginIcon,
  Star as StarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { alpha, useTheme, styled, keyframes } from '@mui/material/styles';
import API from "../../../../API";
import CreatorSelectionCard from './applyCasting/creatorSelectionCard';

// ═══════════════════════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.6);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const GradientButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: 16,
  padding: theme.spacing(2, 5),
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1.1rem",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  ...(buttonVariant === "contained" && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.35)}`,
    color: theme.palette.primary.contrastText,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
      transition: "left 0.5s",
    },
    "&:hover": {
      transform: "translateY(-3px) scale(1.02)",
      boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.45)}`,
      "&::before": {
        left: "100%",
      },
    },
    "&:active": {
      transform: "translateY(-1px) scale(0.98)",
    },
  }),
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 28,
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: '0 24px 96px rgba(0, 0, 0, 0.15)',
    overflow: 'visible',
    // Account for AppShell on mobile
    [theme.breakpoints.down('md')]: {
      marginBottom: 'calc(86px + env(safe-area-inset-bottom, 0px) + 12px)', // AppShell height + safe area + extra gutter
      maxHeight: 'calc(100vh - 86px - env(safe-area-inset-bottom, 0px) - 24px)', // Leave space for AppShell
    },
  },
}));

const IconContainer = styled(Box)(({ theme, color = 'primary' }) => ({
  width: 96,
  height: 96,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.15)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  position: 'relative',
  animation: `${float} 3s ease-in-out infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -4,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
    opacity: 0.2,
    animation: `${pulseGlow} 2s ease-in-out infinite`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    transition: 'all 0.3s ease',
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
    borderWidth: 2,
  },
}));

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function CastingCallApplication({ 
  castingCall, 
  isAuthenticated,
  isCreatorAuthorized,
  hasApplied,
  onSuccess,
  onLoginRequired,
}) {
  const theme = useTheme();
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Agency state
  const [agencyMode, setAgencyMode] = useState(false);
  const [agencyData, setAgencyData] = useState(null);
  const [checkingAgency, setCheckingAgency] = useState(false);
  const [agencyCheckError, setAgencyCheckError] = useState(null);
  
  // Application data
  const [applicationMessage, setApplicationMessage] = useState('');
  const [selectedCreators, setSelectedCreators] = useState(new Set());
  const [creatorMessages, setCreatorMessages] = useState({});
  const [agencyNote, setAgencyNote] = useState('');
  const [creatorSearch, setCreatorSearch] = useState('');

  const isCreatorCall = castingCall?.call_type === 'creator';

  // ═══════════════════════════════════════════════════════════════════════════
  // FILTERED CREATORS (OPTIMIZED WITH USEMEMO)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const filteredCreators = useMemo(() => {
    if (!agencyData?.eligible_creators) return [];
    
    const searchLower = creatorSearch.toLowerCase().trim();
    if (!searchLower) return agencyData.eligible_creators;
    
    return agencyData.eligible_creators.filter(creator => 
      creator.creator.toLowerCase().includes(searchLower) ||
      creator.display_name?.toLowerCase().includes(searchLower)
    );
  }, [agencyData, creatorSearch]);

  const eligibleCreatorsCount = useMemo(() => 
    filteredCreators.filter(c => !c.has_applied).length,
    [filteredCreators]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // CHECK AGENCY STATUS ON MOUNT
  // ═══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (isAuthenticated && isCreatorCall && !hasApplied && castingCall?.public_slug) {
      console.log('🔄 Checking agency status for creator call...');
      checkAgencyStatus();
    }
  }, [isAuthenticated, isCreatorCall, hasApplied, castingCall?.public_slug]);

  const checkAgencyStatus = async () => {
    console.log('🔍 Starting agency status check');
    setCheckingAgency(true);
    setAgencyCheckError(null);
    
    try {
      const response = await API.castingCalls.checkAgencyEligibility(castingCall.public_slug);
      console.log('✅ Agency check response:', response);
      
      if (response.is_agency && response.can_apply && response.eligible_creators?.length > 0) {
        console.log(`🏢 Agency mode enabled: ${response.agency_name} with ${response.eligible_creators.length} creators`);
        setAgencyData(response);
        setAgencyMode(true);
      } else {
        console.log('👤 User is not an agency or has no eligible creators');
        setAgencyData(null);
        setAgencyMode(false);
      }
    } catch (err) {
      console.error('❌ Agency check failed:', err);
      setAgencyCheckError(err?.response?.data?.error || err?.message || 'Failed to check agency status');
      setAgencyData(null);
      setAgencyMode(false);
    } finally {
      setCheckingAgency(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLE APPLICATION SUBMISSION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleApply = async () => {
    if (!isAuthenticated) {
      console.log('🔒 User not authenticated');
      if (onLoginRequired) onLoginRequired();
      return;
    }

    console.log('📤 Submitting application...');
    setError(null);
    setApplying(true);

    try {
      if (agencyMode && agencyData) {
        // AGENCY APPLICATION
        const creator_ids = Array.from(selectedCreators);
        console.log('🏢 Agency application:', { creator_ids, agencyNote, creatorMessages });
        
        if (creator_ids.length === 0) {
          setError('Please select at least one creator');
          setApplying(false);
          return;
        }

        await API.castingCalls.agencyApply(castingCall.id, {
          creator_ids,
          agency_note: agencyNote,
          creator_messages: creatorMessages
        });
        
      } else {
        // INDIVIDUAL APPLICATION
        console.log('👤 Individual application');
        const payload = { message: applicationMessage };
        
        if (isCreatorCall) {
          await API.castingCalls.creatorApply(castingCall.id, payload);
        } else {
          await API.castingCalls.generalApply(castingCall.id, payload);
        }
      }

      console.log('✅ Application successful');
      setShowSuccess(true);
      setTimeout(() => {
        setDialogOpen(false);
        if (onSuccess) onSuccess();
      }, 2000);
      
    } catch (err) {
      console.error('❌ Application failed:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CREATOR SELECTION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const toggleCreator = (creatorId) => {
    const newSelected = new Set(selectedCreators);
    if (newSelected.has(creatorId)) {
      newSelected.delete(creatorId);
      const newMessages = { ...creatorMessages };
      delete newMessages[creatorId];
      setCreatorMessages(newMessages);
    } else {
      newSelected.add(creatorId);
    }
    setSelectedCreators(newSelected);
  };

  const selectAllEligible = () => {
    const eligible = filteredCreators
      .filter(c => !c.has_applied)
      .map(c => c.creator);
    setSelectedCreators(new Set(eligible));
  };

  const clearSelection = () => {
    setSelectedCreators(new Set());
    setCreatorMessages({});
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (checkingAgency) {
    return (
      <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Checking Application Options...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Looking for your managed creators
          </Typography>
        </Box>
      </Fade>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NOT AUTHENTICATED STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (!isAuthenticated) {
    return (
      <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <IconContainer color="primary">
            <LoginIcon sx={{ fontSize: 52, color: 'primary.main' }} />
          </IconContainer>
          
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2, 
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Apply to This Opportunity
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary', 
              mb: 5, 
              lineHeight: 1.8,
              maxWidth: 480,
              mx: 'auto',
              fontSize: '1.05rem',
            }}
          >
            Join Blitz to submit your application and connect with amazing opportunities in the creator economy.
          </Typography>
          
          <GradientButton
            variant="contained"
            fullWidth
            size="large"
            onClick={handleApply}
            startIcon={<LoginIcon />}
            sx={{ 
              maxWidth: 400,
              mx: 'auto',
              display: 'flex',
            }}
          >
            Sign In to Apply
          </GradientButton>
        </Box>
      </Fade>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ALREADY APPLIED STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (hasApplied) {
    return (
      <Zoom in timeout={600}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <IconContainer color="success">
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
          </IconContainer>
          
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2, 
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Application Submitted!
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              lineHeight: 1.8,
              maxWidth: 480,
              mx: 'auto',
              fontSize: '1.05rem',
            }}
          >
            Your application has been successfully submitted. The poster will review it and get back to you soon.
          </Typography>

          <Box sx={{ mt: 4, p: 3, borderRadius: 4, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
              <StarIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2" fontWeight={600} color="success.main">
                Keep an eye on your notifications for updates
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Zoom>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CREATOR CALL - NON-AGENCY RESTRICTION
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (isCreatorCall && !agencyData && !isCreatorAuthorized) {  // ← ADD: && !isCreatorAuthorized
  return (
    <Fade in timeout={600}>
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <IconContainer color="warning">
          <BusinessIcon sx={{ fontSize: 52, color: 'warning.main' }} />
        </IconContainer>
        
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Agency Applications Only
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary', 
            mb: 3, 
            lineHeight: 1.8,
            maxWidth: 480,
            mx: 'auto',
            fontSize: '1.05rem',
          }}
        >
          This is a creator opportunity. Only agencies managing creators can apply on behalf of their talent.
        </Typography>

        {agencyCheckError && (
          <Alert severity="info" sx={{ mt: 3, borderRadius: 3 }}>
            {agencyCheckError}
          </Alert>
        )}

        <Box sx={{ mt: 4, p: 3, borderRadius: 4, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            💡 Are you an agency? Make sure you have creators set up in your account with the 
            <Box component="span" fontWeight={700}> manager </Box>
            field configured.
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
}

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN APPLICATION UI
  // ═══════════════════════════════════════════════════════════════════════════
  
  const showAgencyToggle = agencyData && agencyData.eligible_creators?.length > 0;

  return (
    <>
      <Fade in timeout={600}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2, 
              fontWeight: 800, 
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.7)} 100%)`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Ready to Apply?
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary', 
              mb: 4, 
              textAlign: 'center', 
              lineHeight: 1.8,
              fontSize: '1.05rem',
            }}
          >
            {isCreatorCall
              ? 'Submit your application to be considered for this exciting creator opportunity.'
              : 'Submit your application to be considered for this opportunity.'}
          </Typography>

          {/* Agency Mode Toggle - Only for non-creator calls OR if user is an agency */}
          {showAgencyToggle && !isCreatorCall && (
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.04)} 100%)`,
                border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                    Apply as {agencyData.agency_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You can apply on behalf of{' '}
                    <Box component="span" fontWeight={700} color="info.main">
                      {eligibleCreatorsCount} eligible creator{eligibleCreatorsCount !== 1 ? 's' : ''}
                    </Box>
                  </Typography>
                </Box>
              </Stack>
              
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant={agencyMode ? 'outlined' : 'contained'}
                  startIcon={<PersonIcon />}
                  onClick={() => setAgencyMode(false)}
                  fullWidth
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    ...(agencyMode ? {} : {
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }),
                  }}
                >
                  Apply as Individual
                </Button>
                <Button
                  variant={agencyMode ? 'contained' : 'outlined'}
                  startIcon={<BusinessIcon />}
                  onClick={() => setAgencyMode(true)}
                  fullWidth
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    ...(agencyMode ? {
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    } : {}),
                  }}
                >
                  Apply as Agency
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Main Apply Button */}
          <GradientButton
            variant="contained"
            fullWidth
            size="large"
            onClick={() => setDialogOpen(true)}
            disabled={applying}
            startIcon={applying ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          >
            {applying 
              ? 'Submitting...' 
              : agencyMode 
                ? `Select Creators & Apply (${eligibleCreatorsCount} available)` 
                : 'Apply Now'}
          </GradientButton>
        </Box>
      </Fade>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* APPLICATION DIALOG */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      <StyledDialog
        open={dialogOpen}
        onClose={() => !applying && setDialogOpen(false)}
        maxWidth={agencyMode ? 'md' : 'sm'}
        fullWidth
        TransitionComponent={Zoom}
        transitionDuration={400}
      >
        <DialogTitle sx={{ 
          pb: 2,
          // Mobile optimizations
          [theme.breakpoints.down('md')]: {
            px: 2,
            pt: 2,
            pb: 1.5,
          },
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ 
                mb: 0.5,
                // Mobile optimizations
                [theme.breakpoints.down('md')]: {
                  fontSize: '1.25rem', // Smaller on mobile
                },
              }}>
                {agencyMode ? 'Apply on Behalf of Creators' : 'Submit Application'}
              </Typography>
              {agencyMode && agencyData && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {agencyData.agency_name}
                  </Typography>
                </Stack>
              )}
            </Box>
            <IconButton 
              onClick={() => setDialogOpen(false)} 
              disabled={applying}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ 
          pt: 3,
          // Mobile optimizations
          [theme.breakpoints.down('md')]: {
            px: 2, // Slightly less padding on mobile
            pb: 2, // Bottom padding for scroll area
            maxHeight: 'calc(100vh - 86px - env(safe-area-inset-bottom, 0px) - 200px)', // Account for title + actions + AppShell
            overflowY: 'auto',
            // Better scrolling on mobile
            WebkitOverflowScrolling: 'touch',
          },
        }}>
          {showSuccess ? (
            // SUCCESS STATE
            <Zoom in timeout={600}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    animation: `${scaleIn} 0.6s ease-out, ${float} 3s ease-in-out infinite 0.6s`,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main' }} />
                </Box>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                  Application Submitted!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                  {agencyMode ? 
                    `Successfully submitted applications for ${selectedCreators.size} creator${selectedCreators.size !== 1 ? 's' : ''}` :
                    'Your application is on its way'
                  }
                </Typography>
              </Box>
            </Zoom>
          ) : agencyMode && agencyData ? (
            // AGENCY MODE
            <Stack spacing={4}>
              {/* Creator Search & Selection */}
              <Box>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Select Creators
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" fontWeight={700} color="primary.main">
                        {selectedCreators.size}
                      </Box>{' '}
                      of {eligibleCreatorsCount} selected
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button 
                      size="small" 
                      onClick={selectAllEligible}
                      variant="outlined"
                      disabled={eligibleCreatorsCount === 0}
                      sx={{ 
                        borderRadius: 2.5, 
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                      }}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      onClick={clearSelection}
                      variant="outlined"
                      color="error"
                      disabled={selectedCreators.size === 0}
                      sx={{ 
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                      }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>

                {/* Search Bar */}
                <StyledTextField
                  fullWidth
                  size="small"
                  placeholder="Search creators by name or username..."
                  value={creatorSearch}
                  onChange={(e) => setCreatorSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: creatorSearch && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={() => setCreatorSearch('')}
                          sx={{ mr: -1 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                {/* Search Results Info */}
                {creatorSearch && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`${filteredCreators.length} result${filteredCreators.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                )}

                {/* Creator List */}
                <Stack spacing={2} sx={{ 
                  maxHeight: 400, 
                  overflowY: 'auto', 
                  pr: 1,
                  // Mobile optimizations
                  [theme.breakpoints.down('md')]: {
                    maxHeight: 300, // Shorter on mobile to fit within viewport
                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                  },
                }}>
                  {filteredCreators.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {creatorSearch ? 'No creators match your search' : 'No eligible creators found'}
                      </Typography>
                    </Box>
                  ) : (
                    filteredCreators.map((creator) => (
                      <Box key={creator.creator}>
                        <CreatorSelectionCard
                          creator={creator}
                          selected={selectedCreators.has(creator.creator)}
                          onToggle={() => toggleCreator(creator.creator)}
                          disabled={creator.has_applied}
                        />
                        
                        {selectedCreators.has(creator.creator) && (
                          <Collapse in timeout={300}>
                            <Box sx={{ mt: 2, ml: 2 }}>
                              <StyledTextField
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder={`Optional note for ${creator.display_name || creator.creator}...`}
                                value={creatorMessages[creator.creator] || ''}
                                onChange={(e) => setCreatorMessages({
                                  ...creatorMessages,
                                  [creator.creator]: e.target.value
                                })}
                                inputProps={{ maxLength: 300 }}
                                helperText={`${(creatorMessages[creator.creator] || '').length}/300`}
                              />
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    ))
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Agency Note */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Agency Note (Optional)
                </Typography>
                <StyledTextField
                  multiline
                  rows={3}
                  value={agencyNote}
                  onChange={(e) => setAgencyNote(e.target.value)}
                  placeholder="Add a general message to the brand about these creators..."
                  fullWidth
                  inputProps={{ maxLength: 500 }}
                  helperText={`${agencyNote.length}/500 characters`}
                />
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)}
                  sx={{ borderRadius: 3 }}
                >
                  {error}
                </Alert>
              )}
            </Stack>
          ) : (
            // INDIVIDUAL MODE
            <Stack spacing={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
                  border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MessageIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                      Tell Your Story
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      Share why you're the perfect fit. Highlight your experience, passion, and what makes you unique.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Application message field */}
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Your Application Message
                </Typography>
                <StyledTextField
                  multiline
                  rows={8}
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  placeholder="I would be perfect for this opportunity because..."
                  fullWidth
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${applicationMessage.length}/1000 characters`}
                  autoFocus
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  💡 Tips for a Standout Application
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    'Highlight specific relevant experience and achievements',
                    'Explain why this opportunity excites you',
                    'Show enthusiasm while remaining professional',
                    'Keep it concise but impactful'
                  ].map((tip, idx) => (
                    <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'info.main',
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {tip}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)}
                  sx={{ borderRadius: 3 }}
                >
                  {error}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        {!showSuccess && (
          <>
            <Divider />
            <DialogActions sx={{ 
              p: 3,
              // Mobile optimizations
              [theme.breakpoints.down('md')]: {
                px: 2,
                py: 2.5,
                gap: 1.5, // Better spacing between buttons on mobile
                flexWrap: 'wrap', // Allow wrapping if needed
              },
            }}>
              <Button
                onClick={() => setDialogOpen(false)}
                disabled={applying}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  // Mobile optimizations
                  [theme.breakpoints.down('md')]: {
                    flex: '1 1 auto', // Allow button to grow on mobile
                    minWidth: 100,
                  },
                }}
              >
                Cancel
              </Button>
              <GradientButton
                variant="contained"
                onClick={handleApply}
                disabled={applying || (agencyMode && selectedCreators.size === 0)}
                startIcon={applying ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ 
                  minWidth: 160,
                  py: 1.25,
                  // Mobile optimizations
                  [theme.breakpoints.down('md')]: {
                    flex: '2 1 auto', // Take more space than Cancel on mobile
                    minWidth: 140,
                  },
                }}
              >
                {applying 
                  ? 'Submitting...' 
                  : agencyMode 
                    ? `Submit for ${selectedCreators.size} Creator${selectedCreators.size !== 1 ? 's' : ''}` 
                    : 'Submit Application'}
              </GradientButton>
            </DialogActions>
          </>
        )}
      </StyledDialog>
    </>
  );
}